import { Box, IconButton, TableCell, TableRow, Theme, Typography, useTheme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Fragment, useState } from "react";
import CustomTable from "../../investorOverview/common/customTable";
import { PaginationObj, TableHeaderData } from "../../investorOverview/types";
import { FamilyFeeBreakdownObj, FamilyFeeSummaryObj } from "../../../redux-store/types/onBoarding";
import { toINR } from "../../../utils/utilityFunctions";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";

interface RiaSummaryTableProps {
    isLoading: boolean;
    tableData: Array<FamilyFeeSummaryObj>;
    tablePaginationObj: PaginationObj;
    handleTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleTablePageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
}

const EXPAND_COLUMN_WIDTH = "40px";

const tableHeaderData: TableHeaderData[] = [
    {
        id: "expand",
        label: "",
        align: "center",
        isSortable: false,
        headerTableCellStyle: {
            width: EXPAND_COLUMN_WIDTH,
            minWidth: EXPAND_COLUMN_WIDTH,
            maxWidth: EXPAND_COLUMN_WIDTH,
            padding: "15px 4px",
        },
    },
    {
        id: "familyName",
        label: "Name",
        align: "left",
        isSortable: false,
        headerTableCellStyle: {
            width: "38%",
        },
    },
    {
        id: "status",
        label: "",
        align: "center",
        isSortable: false,
        headerTableCellStyle: {
            width: "18%",
        },
    },
    {
        id: "feeType",
        label: "Fee Type",
        align: "center",
        isSortable: false,
        headerTableCellStyle: {
            width: "22%",
        },
    },
    {
        id: "feeValue",
        label: "Fee Value (Inc GST)",
        align: "center",
        isSortable: false,
        headerTableCellStyle: {
            width: "22%",
        },
    },
];

const INVESTOR_TYPE_COLOR_MAP: Record<string, string> = {
    independent: "success.500",
    dependent: "error.500",
    "non-individual": "warning.500",
};

const getRowStyles = (theme: Theme) => ({
    "& td": {
        paddingTop: "18px",
        paddingBottom: "18px",
        paddingLeft: "16px",
        paddingRight: "16px",
        borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
    },
});

const expandCellSx = {
    width: EXPAND_COLUMN_WIDTH,
    minWidth: EXPAND_COLUMN_WIDTH,
    maxWidth: EXPAND_COLUMN_WIDTH,
    paddingLeft: "8px",
    paddingRight: "4px",
};


const tableLoaderCellStyles = tableHeaderData.map((header, index) => ({
    ...(header.headerTableCellStyle ?? {}),
    ...(index === 0 ? expandCellSx : {}),
    ...(index === 1 ? { paddingLeft: "16px" } : {}),
}));

function HofBadge(): JSX.Element {
    const theme = useTheme();

    return (
        <Box
            component="span"
            sx={{
                padding: "2px 8px",
                borderRadius: "10px",
                backgroundColor: theme.palette.grey[700]
            }}
        >
            <Typography
                variant="subtitle_500"
                sx={{ color: theme.palette.text.primary }}
            >
                HOF
            </Typography>
        </Box>
    );
}

function DataTable({
    tableData,
    expandedRows,
    onToggleRow,
}: {
    tableData: Array<FamilyFeeSummaryObj>;
    expandedRows: Array<string>;
    onToggleRow: (selectedFamilyId: string) => void;
}): JSX.Element {
    const theme = useTheme();

    // Data from Redux
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {tableData.map((rowData: FamilyFeeSummaryObj) => {
                const isExpanded = expandedRows.includes(rowData.familyId);
                const hasFamilyMembers = (rowData.familyFeeBreakdown ?? []).length > 0;

                return (
                    <Fragment key={rowData.familyId}>
                        <TableRow sx={getRowStyles(theme)}>
                            <TableCell align="center" sx={expandCellSx}>
                                {hasFamilyMembers ? (
                                    <IconButton
                                        size="small"
                                        onClick={() => onToggleRow(rowData.familyId)}
                                        aria-label={isExpanded ? "Collapse row" : "Expand row"}
                                        sx={{
                                            padding: "4px",
                                            color: theme.palette.text.primary,
                                            "&:hover": {
                                                backgroundColor: "transparent",
                                            },
                                        }}
                                    >
                                        {isExpanded ? (
                                            <KeyboardArrowDownIcon fontSize="small" />
                                        ) : (
                                            <KeyboardArrowRightIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                ) : null}
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {rowData?.familyName ?? "-"} {rowData?.familyId ? "(" + rowData?.familyId + ")" : ""}
                                </Typography>
                            </TableCell>
                            <TableCell align="center" />
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {rowData?.feeType ?? "-"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        lineHeight: "20px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {(rowData?.feeValue !== null && rowData?.feeValue !== undefined) ? toINR({ value: rowData?.feeValue ?? "", locale, currencyCode }) : "-"}
                                </Typography>
                            </TableCell>
                        </TableRow>
                        {isExpanded &&
                            (rowData.familyFeeBreakdown ?? []).map((subRowData: FamilyFeeBreakdownObj) => (
                                <TableRow key={`${rowData.familyId}-${subRowData.customer}`} sx={getRowStyles(theme)}>
                                    <TableCell align="center" sx={expandCellSx} />
                                    <TableCell align="left">
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                columnGap: "8px",
                                                paddingLeft: "16px",
                                            }}
                                        >
                                            <Typography
                                                variant="body_500"
                                                sx={{
                                                    lineHeight: "20px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.text.primary,
                                                }}
                                            >
                                                {subRowData?.customerName ?? "-"} {subRowData?.customer ? "(" + subRowData?.customer + ")" : ""}
                                            </Typography>
                                            {["y"].includes(`${subRowData?.isFamilyHead}`.toLowerCase()) && <HofBadge />}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body_500"
                                            sx={{
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: INVESTOR_TYPE_COLOR_MAP[`${subRowData?.investorType ?? ""}`.toLowerCase()] ??
                                                    theme.palette.text.primary,
                                            }}
                                        >
                                            {subRowData?.investorType ?? "-"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body_500"
                                            sx={{
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.primary,
                                            }}
                                        >
                                            {subRowData?.feeType ?? "-"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body_500"
                                            sx={{
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.primary,
                                            }}
                                        >
                                            {(subRowData?.feeValue !== null && subRowData?.feeValue !== undefined) ? toINR({ value: subRowData?.feeValue ?? "", locale, currencyCode }) : "-"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </Fragment>
                );
            })}
        </>
    );
}

export default function RiaSummaryTable({
    isLoading,
    tableData,
    tablePaginationObj,
    handleTableRowsPerPageChange,
    handleTablePageChange,
}: RiaSummaryTableProps): JSX.Element {

    // State
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    const handleToggleRow = (selectedFamilyId: string) => {
        setExpandedRows((prevState) =>
            prevState.includes(selectedFamilyId) ? prevState.filter((id) => id !== selectedFamilyId) : [...prevState, selectedFamilyId]
        );
    };

    return (
        <CustomTable
            isLoading={isLoading}
            tableBody={
                <DataTable
                    tableData={tableData}
                    expandedRows={expandedRows}
                    onToggleRow={handleToggleRow}
                />
            }
            tableData={tableData}
            tableHeaderData={tableHeaderData}
            tableContainerStyles={{
                "& table": {
                    tableLayout: "fixed",
                    width: "100%",
                },
            }}
            tableHeadRowCustomStyles={{
                "& th:nth-of-type(2)": {
                    paddingLeft: "16px",
                },
            }}
            tableLoaderCellStyles={tableLoaderCellStyles}
            tableBodyCustomStyles={{
                "& tr:last-child td": {
                    border: 0,
                },
            }}
            loadingRowsNum={7}
            tableDataCount={tablePaginationObj.totalRows}
            rowsPerPage={tablePaginationObj.rowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 20]}
            handleRowsPerPageChange={handleTableRowsPerPageChange}
            handlePageChange={handleTablePageChange}
            currentPage={tablePaginationObj.currentPage}
        />
    );
}
